import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Field } from "../common";

export function useRunWatcher(onDone) {
  const [running, setRunning] = useState(false);
  const timer = useRef();
  const done = useRef(onDone);
  done.current = onDone;
  const watch = (id) => {
    setRunning(true);
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/leadgen/runs/${id}`);
        if (r.data.status !== "running") { clearInterval(timer.current); setRunning(false); done.current(r.data); }
      } catch { clearInterval(timer.current); setRunning(false); }
    }, 2500);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return { running, watch };
}

export const REVIEW_LABEL = { pending: "Awaiting approval", below_threshold: "Below threshold", approved: "Approved", rejected: "Rejected", merged: "Merged", approving: "Approving" };

export const BarList = ({ data = [], testid, empty = "No data yet" }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (!data.length) return <div className="text-sm text-muted-foreground py-4" data-testid={testid}>{empty}</div>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {data.map((d) => (
        <div key={d.name} className="text-sm">
          <div className="flex justify-between gap-2"><span className="truncate">{d.name}</span><span className="font-num font-semibold">{d.value}</span></div>
          <div className="h-1.5 rounded-full bg-violet-50 mt-1 overflow-hidden"><div className="h-full vmw-gradient rounded-full transition-[width] duration-500" style={{ width: `${(d.value / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

export const ScorePill = ({ score }) => {
  const tone = score >= 75 ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : score >= 50 ? "bg-sky-50 text-sky-700 ring-sky-200" : "bg-slate-100 text-slate-600 ring-slate-200";
  return <span className={`inline-flex font-num items-center rounded-md px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${tone}`}>{score}</span>;
};

export const EMPTY_PROSPECT = { business_name: "", contact_name: "", category: "", city: "", district: "", state: "", country: "", phone: "", email: "", website: "", profile_url: "", social_urls: [], source_url: "", sales_notes: "", followup_date: "" };

const FIELDS = [
  ["business_name", "Business name *"], ["contact_name", "Owner / contact name"], ["category", "Business category"], ["phone", "Public business phone"],
  ["email", "Public business email"], ["website", "Official website"], ["profile_url", "Public business profile URL"], ["city", "City"],
  ["district", "District"], ["state", "State"], ["country", "Country"], ["source_url", "Source URL (where you found it)"],
];

export function ProspectFields({ f, setF, prefix }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {FIELDS.map(([k, label]) => (
        <Field key={k} label={label}><input data-testid={`${prefix}-${k}`} className="vmw-input" value={f[k] || ""} onChange={set(k)} /></Field>
      ))}
      <Field label="Social profile URLs (one per line)" className="sm:col-span-2">
        <textarea data-testid={`${prefix}-social_urls`} className="vmw-input h-16 py-2" value={(f.social_urls || []).join("\n")}
          onChange={(e) => setF({ ...f, social_urls: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} />
      </Field>
      <Field label="Follow-up date"><input type="date" data-testid={`${prefix}-followup_date`} className="vmw-input" value={f.followup_date || ""} onChange={set("followup_date")} /></Field>
      <Field label="Sales notes" className="sm:col-span-2"><textarea data-testid={`${prefix}-sales_notes`} className="vmw-input h-16 py-2" value={f.sales_notes || ""} onChange={set("sales_notes")} /></Field>
    </div>
  );
}

export const assigneeOptions = (sales) => [{ value: "__rr", label: "Round-robin (sales team)" }, ...sales.map((u) => ({ value: u.id, label: u.name }))];
export const assignPayload = (v) => (v === "__rr" ? { round_robin: true } : v ? { assign_to: v } : {});
